import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import './index.scss';

import { errorNormalizer } from '../../../../shared/Helpers/functions';
import AddTarget from './AddTarget';
import ActiveTargets from './ActiveTargets';
import SessionDetails from './sessionDetails';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { getPromptCodes } from '../../../../redux/State/clientSlice/dataCollectionSlice';
import {
	getAllActiveClientTargets,
	getSessionInfo,
	getSessionTargets,
} from '../../../../redux/State/clientSlice/sessionSlice';
import { isElcFunder } from '../../../../redux/API/constants';

const Targets = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId, sessionId } = useParams();

	const promptsData = useSelector((state) => state.dataCollection.promptCodes);
	const data = useSelector((state) => state.session.sessionInfo);

	useEffect(() => {
		if (!sessionId) return;
		dispatch(getSessionInfo(sessionId));
	}, [sessionId, dispatch]);

	useEffect(() => {
		if (promptsData.length) return;
		dispatch(getPromptCodes());
	}, [promptsData.length, dispatch]);

	const fetchActiveTargets = useCallback(() => {
		if (!sessionId) return;
		dispatch(getSessionTargets(sessionId));
	}, [sessionId, dispatch]);

	const fetchData = useCallback(() => {
		if (!clientId) return;
		// Session-scoped goal access: ELC sessions show ELC targets; insurance AND DOE sessions
		// both share the default (null) pool, so any non-ELC session normalizes its funder to null.
		const funderId = isElcFunder(data.sessionType?.funderId) ? data.sessionType?.funderId : null;
		dispatch(getAllActiveClientTargets({ clientId, funderId }))
			.then(unwrapResult)
			.then(() => {
				fetchActiveTargets();
			})
			.catch(errorNormalizer);
	}, [clientId, data.sessionType?.funderId, dispatch, fetchActiveTargets]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		let start = new Date();
		const interval = setInterval(() => {
			const now = new Date();
			const diff = Math.floor((now.getTime() - start.getTime()) / 60000);

			if (diff > 30) {
				start = new Date(start.setHours(start.getHours() + 1));
				toast('Please remember to have at least three targets and behaviors per hours!');
			}
		}, 900000);

		return () => clearInterval(interval);
	}, []);

	return (
		<div className="dataCollectionWrapper">
			<SessionDetails data={data} />
			<AddTarget />
			<ActiveTargets fetchData={fetchData} />
		</div>
	);
};

export default Targets;
